import { PageHeader } from '@/components/ui';
import { useAuth } from '@/contexts/auth-context';
import { fetchProgramById, useProgramQuery } from '@/hooks/queries/use-affiliate-program-query';
import { ensureValidToken } from '@/libs/auth-guard';
import useNavBarStore from '@/stores/nav-store';
import { Container, VStack } from '@chakra-ui/react';
import { BrandAccountType, type Program } from '@fluenceur/types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { useLoaderData } from 'react-router-dom';
import AffiliateProgramStats from './AffiliateProgram.Stats';
import ProgramDangerZone from './ProgramDangerZone';
import AffiliateProgramOverviewDetails from './ProgramOverview.Details';
import AffiliateProgramOverviewDiscountCodes from './ProgramOverview.DiscountCodes';
import ProgramOverviewRewards from './ProgramOverview.Rewards';
import ProgramTrackingCard from './ProgramTrackingCard';

interface ProgramLoaderData {
  program: Program;
}

const ProgramOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { program: programFromLoader } = useLoaderData() as ProgramLoaderData;

  const { data: program, isLoading } = useProgramQuery({
    programId: programFromLoader.id,
    initialData: programFromLoader,
  });

  const { setData } = useNavBarStore();

  useEffect(() => {
    // Seed the program panel's title. The panel itself is opened by the sidebar
    // from the /programs/:id route.
    const currentProgram = program || programFromLoader;
    if (currentProgram) {
      setData({ id: currentProgram.id, name: currentProgram.name });
    }
  }, [program, programFromLoader, setData]);

  // Determine the program to display. Prioritize hook's data.
  const displayProgram = program || programFromLoader;

  if (isLoading && !displayProgram) {
    return <p>{t('common.loading')}</p>; // Show loading only if no data at all is available
  }

  if (!displayProgram) {
    // This case should ideally not be hit if loader ensures programFromLoader exists
    // and hook eventually fetches or uses initialData.
    return <p>Program not found.</p>;
  }

  return (
    <Container maxW="7xl" py={10}>
      <PageHeader
        title={t('programs.programOverview.title')}
        description={t('programs.programOverview.description')}
      />
      <VStack gap={8}>
        <AffiliateProgramStats programId={displayProgram.id} />
        <AffiliateProgramOverviewDetails program={displayProgram} />
        {user?.currentBrand?.accountType === BrandAccountType.PARTNER &&
          user.currentBrand.id === displayProgram.brandId && (
            <ProgramTrackingCard brandId={user.currentBrand.id} programId={displayProgram.id} />
          )}
        <AffiliateProgramOverviewDiscountCodes program={displayProgram} />
        <ProgramOverviewRewards program={displayProgram} />
        <ProgramDangerZone program={displayProgram} />
      </VStack>
    </Container>
  );
};

export const programLoader = async ({ params }: LoaderFunctionArgs) => {
  await ensureValidToken();

  const { id } = params;
  if (!id) throw new Response('Not Found', { status: 404 });

  // Dynamically import the fetch function from the hook file for the loader
  const programData = await fetchProgramById(id);

  if (!programData) {
    throw new Response('Program Not Found', { status: 404 });
  }

  return { program: programData };
};

export { programLoader as affiliateProgramLoader };

export default ProgramOverviewPage;
